import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { GetChatsAction } from '@smatchy/domains/message';
import { LocalActivePushNotificationAdapter } from './local-active-push-notification.adapter';
import { SetPushTokenAction } from '../core/domains/user/state/user-notification.actions';
import { ActionPerformed, PushNotifications, PushNotificationSchema } from '@capacitor/push-notifications';
import { NotificationActionRequest } from '@smatchy/notification';
import { firstValueFrom } from 'rxjs';
import { MarkNotificationReadAction } from '@smatchy/core';
import { LoadUserProfileAction } from '../core/domains/user/state/user-profile.actions';
import { FindProOrganizerRequestByUser } from '../core/domains/pro-organizer-request/state/find-by-user/find-by-user.actions';
import { navigateFromNotificationAction } from '../core/utils/notification-routing.util';
import { AppIconBadgeService } from '../core/services/app-icon-badge.service';
import { PushOpenReporter } from '../core/domains/app/push-open-reporter.service';
import { captureException } from '../tracing';

type NotificationPayload = {
  action: string;
  id: string;
  type: string;
};

@Injectable({
  providedIn: 'root',
})
export class PushNotificationAdapter {
  private hasActionPerformedListener = false;
  private hasNotificationReceivedListener = false;
  private hasRegistrationListener = false;
  private isSyncingPushToken = false;
  private lastSyncedPushToken = '';
  private lastNotificationOpenStartedAt = 0;
  private notificationOpenWaiters: Array<(started: boolean) => void> = [];

  constructor(
    private readonly _store: Store,
    private readonly _router: Router,
    private readonly _localActivePushNotificationAdapter: LocalActivePushNotificationAdapter,
    private readonly _appIconBadgeService: AppIconBadgeService,
    private readonly _pushOpenReporter: PushOpenReporter,
  ) {}

  async addListener() {
    await this.ensureNotificationListeners();
    await this.ensureRegistrationListeners();
  }

  async registerIfPermitted() {
    await this.ensureRegistrationListeners();

    if (!(await this.isPermitted())) {
      return;
    }

    await PushNotifications.register();
  }

  /** Whether the system already lets the app notify, without asking anything. */
  async isPermitted(): Promise<boolean> {
    const pushPermission = await PushNotifications.checkPermissions();
    const firebasePermission = await FirebaseMessaging.checkPermissions();

    return pushPermission.receive === 'granted' && firebasePermission.receive === 'granted';
  }

  async requestPermission() {
    const pushPermission = await PushNotifications.checkPermissions();

    if (pushPermission.receive !== 'granted') {
      const requestedPushPermission = await PushNotifications.requestPermissions();

      if (requestedPushPermission.receive !== 'granted') {
        return;
      }
    }

    const permission = await FirebaseMessaging.checkPermissions();

    if (permission.receive !== 'granted') {
      const requestedPermission = await FirebaseMessaging.requestPermissions();

      if (requestedPermission.receive !== 'granted') {
        return;
      }
    }

    await this.ensureRegistrationListeners();
    await PushNotifications.register();
  }

  async addActionPerformedListener() {
    if (this.hasActionPerformedListener) {
      return;
    }
    this.hasActionPerformedListener = true;
    await PushNotifications.addListener('pushNotificationActionPerformed', (notification: ActionPerformed) => {
      console.log('pushNotificationActionPerformed');
      const dataEvent = notification.notification.data as NotificationActionRequest;
      void this.handleNotificationOpened(dataEvent);
    });
  }

  hasRecentNotificationOpen(windowMs = 2000): boolean {
    return Date.now() - this.lastNotificationOpenStartedAt < windowMs;
  }

  waitForNotificationOpen(timeoutMs: number): Promise<boolean> {
    if (this.hasRecentNotificationOpen()) {
      return Promise.resolve(true);
    }

    return new Promise<boolean>((resolve) => {
      const waiter = (started: boolean) => {
        clearTimeout(timeout);
        resolve(started);
      };
      const timeout = window.setTimeout(() => {
        this.notificationOpenWaiters = this.notificationOpenWaiters.filter((item) => item !== waiter);
        resolve(false);
      }, timeoutMs);

      this.notificationOpenWaiters.push(waiter);
    });
  }

  private async ensureNotificationListeners() {
    if (this.hasNotificationReceivedListener) {
      return;
    }
    this.hasNotificationReceivedListener = true;
    await PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
      console.log('pushNotificationReceived', { notification });
      void this.syncBadgeFromBackend();
      const dataEvent = notification.notification.data as NotificationPayload;
      const isChatNotification = dataEvent?.type === 'chat' || dataEvent?.action === 'routing_event' || !!dataEvent?.id;
      if (isChatNotification) {
        this._store.dispatch(new GetChatsAction());
        this._localActivePushNotificationAdapter.showNotificationChat({
          title: notification.title || notification.body || '',
          body: notification.body || '',
          id: dataEvent?.id || '',
        });
      }
    });
    await FirebaseMessaging.addListener('notificationReceived', async (event) => {
      console.log('notificationReceived', { event });
      void this.syncBadgeFromBackend();
      const dataEvent = event.notification.data as NotificationPayload;
      const isChatNotification = dataEvent?.type === 'chat' || dataEvent?.action === 'routing_event' || !!dataEvent?.id;
      if (isChatNotification) {
        this._store.dispatch(new GetChatsAction());
        this._localActivePushNotificationAdapter.showNotificationChat({
          title: event.notification.title || event.notification.body || '',
          body: event.notification.body || '',
          id: dataEvent?.id || '',
        });
      }
    });
  }

  private syncBadgeFromBackend(): void {
    this._appIconBadgeService.syncFromBackend().catch((error) => {
      console.error('unableToSyncAppIconBadgeFromPush', error);
    });
  }

  private async handleNotificationOpened(dataEvent: NotificationActionRequest): Promise<void> {
    // When pushes are opened, for sending at the right time. Fired and
    // forgotten, and nothing is sent if the usage measurement was refused.
    this._pushOpenReporter.report(dataEvent?.type);

    if (dataEvent?.action !== 'routing_event') {
      void this.completeNotificationOpenSideEffects(dataEvent);
      return;
    }

    this.markNotificationOpenStarted();
    await this.prepareNotificationRoutingData(dataEvent).catch((error) => {
      console.error('unableToPrepareNotificationRoutingData', error);
    });
    navigateFromNotificationAction(this._router, dataEvent);
    void this.completeNotificationOpenSideEffects(dataEvent);
  }

  private markNotificationOpenStarted(): void {
    this.lastNotificationOpenStartedAt = Date.now();
    const waiters = this.notificationOpenWaiters.splice(0);
    waiters.forEach((resolve) => resolve(true));
  }

  private async completeNotificationOpenSideEffects(dataEvent: NotificationActionRequest): Promise<void> {
    const inboxId = dataEvent?.inboxId;
    if (inboxId) {
      await firstValueFrom(this._store.dispatch(new MarkNotificationReadAction(inboxId))).catch((error) => {
        console.error('unableToMarkNotificationReadFromPush', error);
      });
    }
    await this._appIconBadgeService.syncFromBackend().catch((error) => {
      console.error('unableToSyncAppIconBadgeFromPushOpen', error);
    });
  }

  private async prepareNotificationRoutingData(dataEvent: NotificationActionRequest): Promise<void> {
    if (dataEvent?.action !== 'routing_event') {
      return;
    }
    if (
      dataEvent.type === 'premium-permission' ||
      dataEvent.type === 'request' ||
      dataEvent.type === 'insurance-renewal-required'
    ) {
      /*
       * CAUGHT, like every other dispatch in this file.
       *
       * These two were the only ones left bare, and they run from a push
       * listener: nothing above them catches, so a failure here becomes an
       * unhandled rejection rather than an error somebody handles. A
       * notification opened while the session is gone answers 401 on both, and
       * the person ends up with the routing they asked for silently abandoned.
       *
       * Loading the profile is preparation for routing, not the routing
       * itself. Failing it should cost the preparation, never the navigation.
       */
      await firstValueFrom(this._store.dispatch(new LoadUserProfileAction())).catch((error) => {
        console.error('unableToLoadProfileForNotificationRouting', error);
      });
      await firstValueFrom(this._store.dispatch(new FindProOrganizerRequestByUser())).catch((error) => {
        console.error('unableToLoadProRequestForNotificationRouting', error);
      });
    }
  }

  private async ensureRegistrationListeners() {
    if (this.hasRegistrationListener) {
      return;
    }
    this.hasRegistrationListener = true;
    await this.ensureNotificationListeners();
    await PushNotifications.addListener('registration', async () => {
      await this.syncPushTokenWithRetry();
    });
    await PushNotifications.addListener('registrationError', (error) => {
      console.error('pushNotificationRegistrationError', error);
    });
  }

  private async syncPushTokenWithRetry() {
    if (this.isSyncingPushToken) {
      return;
    }
    this.isSyncingPushToken = true;
    /*
     * FOUR ATTEMPTS ON EVERY PLATFORM, and a failure that reaches Sentry.
     *
     * Android used to get a single attempt, and its failure only went to the
     * console. On 30/10/2026 a tester's Android phone on uat made 1207 calls to
     * the API over two days and never once sent its push token, so no push
     * could ever reach it, and nothing anywhere said so. Whether she declined
     * the permission or getToken failed could not be told apart afterwards.
     */
    const maxAttempts = 4;

    try {
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const result = await FirebaseMessaging.getToken();
          const token = result.token;
          if (!token) {
            if (attempt < maxAttempts) {
              await this.wait(500 * attempt);
              continue;
            }
            this.reportPushTokenNotSynced(new Error('Firebase gave no push token'), attempt);
            return;
          }

          if (token === this.lastSyncedPushToken) {
            return;
          }

          await firstValueFrom(this._store.dispatch(new SetPushTokenAction(token)));
          // Remembered only once the API has it: remembered before, a failed
          // send made the next attempt believe the token was already synced.
          this.lastSyncedPushToken = token;
          return;
        } catch (error) {
          if (attempt === maxAttempts) {
            this.reportPushTokenNotSynced(error, attempt);
            return;
          }
          await this.wait(500 * attempt);
        }
      }
    } finally {
      this.isSyncingPushToken = false;
    }
  }

  private reportPushTokenNotSynced(error: unknown, attempts: number): void {
    console.error('unableToSyncPushToken', error);
    captureException(error, {
      level: 'warning',
      tags: { push: 'token_sync', platform: Capacitor.getPlatform() },
      extra: { attempts },
    });
  }

  private wait(durationInMs: number) {
    return new Promise<void>((resolve) => {
      window.setTimeout(resolve, durationInMs);
    });
  }
}
